import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { AppServices } from '../../servicelayer/app-services';
import { appRouteConstants } from '../../app.route.constants';
import { InitializationStatusCodes } from 'portalServices';

@Injectable({ providedIn: 'root' })
export class AppInitializationService
{
    private initCompleted = new BehaviorSubject<boolean>(false);
    private autoRedirectDisabled = false;

    constructor(
        private appServices: AppServices,
        private router: Router
    )
    {
        this.initializeApp();
        this.trackSuccessfulNavigations();
    }

    private getCurrentUrl(): string
    {
        if (typeof window !== 'undefined' && window.location)
        {
            return `${window.location.pathname}${window.location.search}`;
        }
        return this.router.url;
    }

    public get authenticationService()
    {
        const currentUrl = this.getCurrentUrl();
        if (currentUrl.includes('login')) { return null; }
        return this.appServices.authenticationService;
    }

    public get initializationService()
    {
        const currentUrl = this.getCurrentUrl();
        if (currentUrl.includes('login')) { return null; }
        return this.appServices.initializationService;
    }

    private initializeApp(): void
    {
        const currentUrl = this.getCurrentUrl();

        if (currentUrl.includes('login'))
        {
            this.initCompleted.next(true);
            return;
        }

        const initSvc = this.appServices.initializationService;
        if (!initSvc) { this.initCompleted.next(true); return; }

        combineLatest([initSvc.initState])
            .pipe(
                filter(([state]) =>
                    state === InitializationStatusCodes.RUNNING ||
                    state === InitializationStatusCodes.UNAUTHENTICATED),
                take(1),
                tap(([state]) =>
                {
                    this.initCompleted.next(true);
                    if (state === InitializationStatusCodes.RUNNING && !this.autoRedirectDisabled)
                    {
                        const url = this.getCurrentUrl();
                        if (url === '/' || url === `/${appRouteConstants.AUTH.LOGIN}`)
                        {
                            this.router.navigate([`/${appRouteConstants.DASHBOARD}`]);
                        }
                    }
                    else if (state === InitializationStatusCodes.UNAUTHENTICATED)
                    {
                        this.router.navigate([`/${appRouteConstants.AUTH.LOGIN}`]);
                    }
                })
            )
            .subscribe();
    }

    private trackSuccessfulNavigations(): void
    {
        this.router.events
            .pipe(filter(e => e instanceof NavigationEnd))
            .subscribe((e: NavigationEnd) =>
            {
                if (!e.url.includes(appRouteConstants.AUTH.LOGIN))
                {
                    this.autoRedirectDisabled = false;
                }
            });
    }
}
